/**
 * FieldBox.js
 *
 * Visual representation of a marked variable field on the PDF canvas.
 * Displays the variable label at its position and supports:
 *  - Drag to reposition
 *  - Click to select
 *  - Delete button on hover
 *
 * Props:
 *  - field: { key, label, uiX, uiY, page, font_size }
 *  - selected: boolean
 *  - onSelect: () => void
 *  - onDragEnd: (newX, newY) => void
 *  - onDelete: () => void
 */

import React, { useRef, useState, useCallback } from 'react';

const FieldBox = ({ field, selected, onSelect, onDragEnd, onDelete }) => {
  const boxRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();

    const rect = boxRef.current?.parentElement?.getBoundingClientRect();
    if (!rect) return;

    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      startX: field.uiX,
      startY: field.uiY,
    };
    setDragging(true);

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - dragStart.current.x;
      const dy = moveEvent.clientY - dragStart.current.y;
      const newX = Math.max(0, dragStart.current.startX + dx);
      const newY = Math.max(0, dragStart.current.startY + dy);

      // Live repositioning via direct style mutation for performance
      if (boxRef.current) {
        boxRef.current.style.left = `${newX}px`;
        boxRef.current.style.top = `${newY}px`;
      }
    };

    const handleMouseUp = (upEvent) => {
      const dx = upEvent.clientX - dragStart.current.x;
      const dy = upEvent.clientY - dragStart.current.y;
      const newX = Math.max(0, dragStart.current.startX + dx);
      const newY = Math.max(0, dragStart.current.startY + dy);

      setDragging(false);
      onDragEnd(newX, newY);

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [field.uiX, field.uiY, onSelect, onDragEnd]);

  const fontSize = field.font_size || 12;

  return (
    <div
      ref={boxRef}
      className={`group absolute flex items-center gap-1 select-none transition-shadow ${
        dragging ? 'cursor-grabbing z-50' : 'cursor-grab'
      }`}
      style={{
        left: `${field.uiX}px`,
        top: `${field.uiY}px`,
        zIndex: selected ? 40 : 10,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Field Label Chip */}
      <div
        className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap border transition ${
          selected
            ? 'bg-indigo-100 text-indigo-800 border-indigo-400 shadow-md ring-2 ring-indigo-300'
            : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
        }`}
        style={{ fontSize: `${Math.min(fontSize, 14)}px` }}
      >
        {`{{${field.key}}}`}
        <span className="ml-1 text-[9px] opacity-60">
          ({fontSize}pt)
        </span>
      </div>

      {/* Delete button — visible on hover */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="opacity-0 group-hover:opacity-100 p-0.5 rounded bg-red-500 text-white hover:bg-red-600 transition-opacity"
        title="Remove field"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default FieldBox;
